import { Button as HeadlessButton, type ButtonProps as HeadlessButtonProps } from '@headlessui/react';
import { cn } from '../../lib/utils';

export interface ButtonProps extends HeadlessButtonProps {
  variant?: 'default' | 'primary' | 'destructive' | 'warning' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  children?: React.ReactNode;
}

export function Button({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: ButtonProps) {
  return (
    <HeadlessButton
      className={cn(
        // Base styles: Elastic transition, no focus ring by default
        'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
        'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25',
        'active:scale-95 disabled:pointer-events-none disabled:opacity-50',

        // Variant styles
        {
          // Default: Neutral (Gray), used for "Refresh", "Cancel", etc.
          'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white': variant === 'default',

          // Primary: Emphasis (Green/Brand), used for "Export", "Confirm"
          'bg-green-500/10 text-green-400 hover:bg-green-500/20': variant === 'primary',

          // Warning: Alert (Yellow)
          'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20': variant === 'warning',

          // Destructive: Warning/Danger (Red)
          'bg-red-500/10 text-red-400 hover:bg-red-500/20': variant === 'destructive',

          // Secondary (same as default for now, but keeping for compatibility)
          'bg-white/5 text-white data-[hover]:bg-white/10': variant === 'secondary',

          // Outline
          'bg-transparent text-white ring-1 ring-white/10 data-[hover]:bg-white/5': variant === 'outline',

          // Ghost: No background
          'text-white/70 hover:text-white hover:bg-white/5': variant === 'ghost',

          'text-white/60 underline-offset-4 hover:text-white hover:underline': variant === 'link',
        },
        
        // Size styles
        {
          'h-9 px-4 py-2 text-sm': size === 'default',
          'h-8 px-3 text-xs': size === 'sm',
          'h-11 px-8 text-base': size === 'lg',
          'h-9 w-9 p-0': size === 'icon',
        },
        
        className
      )}
      {...props}
    />
  );
}